/**
 * @file Client wrapper for the `recomputeAnalytics` callable, "Recompute
 * now": asks the service to start the `analytics-drain` job for the tenant and
 * settles to an outcome, never throwing. Consumer: `AnalyticsRecomputeButton`.
 * The request is empty; the callable alone decides the cooldown, and each
 * response field is checked rather than asserted.
 */
import { httpsCallable } from 'firebase/functions';

import { functions } from '@/services/firebaseFunctions';
import type { AnalyticsRecomputeCallOutcome } from '@/analytics';

import { analyticsCallFailure } from './analyticsCallFailure';

export const recomputeAnalytics =
  async (): Promise<AnalyticsRecomputeCallOutcome> => {
    try {
      const callable = httpsCallable<Record<string, never>, unknown>(
        functions,
        'recomputeAnalytics',
      );
      const { data } = await callable({});
      if (
        typeof data !== 'object' ||
        data === null ||
        !('status' in data) ||
        (data.status !== 'started' && data.status !== 'notStarted') ||
        !('requestedAt' in data) ||
        typeof data.requestedAt !== 'string' ||
        !('retryAt' in data) ||
        typeof data.retryAt !== 'string'
      )
        return {
          status: 'failed',
          message: 'The recompute answer arrived in an unexpected shape.',
        };
      return {
        status: 'done',
        response: {
          status: data.status,
          requestedAt: data.requestedAt,
          retryAt: data.retryAt,
        },
      };
    } catch (error) {
      return analyticsCallFailure(error);
    }
  };
